import { useEffect, useMemo, useState } from "react";
import { stepTestNamesFromCommitFiles } from "../../shared/stepTests/constants";
import {
  computeStepTestRows,
  rollupStepTestStatus,
  type StepTestDotStatus,
  stepContentHashFromFiles,
  testIoHashFromFiles,
} from "../../shared/stepTests/freshness";

type CommitFile = { readonly path: string; readonly blobHash: string };

type Spec = {
  readonly name: string;
  readonly status: "passed" | "failed" | "skipped";
  readonly message: string | null;
  readonly inputOutputHash: string | null;
};

type Execution = {
  readonly stepDir: string | null;
  readonly status: "pending" | "success" | "error";
  readonly contentHash: string | null;
  readonly testSpecs: readonly Spec[];
};

export function useStepTestStatusByDir({
  commitFiles,
  stepDirs,
  executions,
}: {
  commitFiles: readonly CommitFile[];
  stepDirs: readonly string[];
  executions: readonly Execution[];
}): ReadonlyMap<string, StepTestDotStatus> {
  const [hashesByDir, setHashesByDir] = useState<ReadonlyMap<
    string,
    {
      codeHash: string;
      ioHashByName: Map<string, string>;
      testNames: string[];
    }
  > | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const entries = await Promise.all(
        stepDirs.map(async (stepDir) => {
          const codeHash = await stepContentHashFromFiles(commitFiles, stepDir);
          const testNames = stepTestNamesFromCommitFiles(commitFiles, stepDir);
          const ioEntries = await Promise.all(
            testNames.map(
              async (name) =>
                [
                  name,
                  await testIoHashFromFiles(commitFiles, stepDir, name),
                ] as const
            )
          );
          return [
            stepDir,
            { codeHash, ioHashByName: new Map(ioEntries), testNames },
          ] as const;
        })
      );
      if (!cancelled) setHashesByDir(new Map(entries));
    })();
    return () => {
      cancelled = true;
    };
  }, [commitFiles, stepDirs]);

  return useMemo(() => {
    const result = new Map<string, StepTestDotStatus>();
    for (const stepDir of stepDirs) {
      const h = hashesByDir?.get(stepDir);
      if (!h) {
        result.set(stepDir, "notrun");
        continue;
      }
      const stepExecutions = executions.filter((e) => e.stepDir === stepDir);
      const { rows } = computeStepTestRows({
        testNames: h.testNames,
        codeHash: h.codeHash,
        ioHashByName: h.ioHashByName,
        executions: stepExecutions,
      });
      result.set(stepDir, rollupStepTestStatus(rows));
    }
    return result;
  }, [hashesByDir, stepDirs, executions]);
}
